import React from 'react';

import {
  ErrorBoundary,
  SearchProvider,
  Results as AppSearchResults,
  WithSearch,
} from '@elastic/react-search-ui';
import { ConfigHelper } from '../../config/ConfigHelper';
import HeApiConnector from '../../connectors/HeApiConnector';
import { ConnectorProvider } from '../../connectors/ConnectorContext';
import DocumentMetaUpdater from '../../components/DocumentMetaUpdater';
import configJson from './teaching-activities-search-config.json';
import PagingRealistic from '../../components/PagingRealistic';
import TeachingActivitiesSearchResultView from './TeachingActivitiesSearchResultView';
import Layout from '../../components/Layout';
import TeachingActivitiesSearchFilters from './TeachingActivitiesSearchFilters';
import Results from '../../components/Results';
import ResultHeader from '../../components/ResultHeader';
import NoResults from '../../components/NoResults';
import Header from '../../components/Header';
import FiltersButton from '../../components/FiltersButton';
import ErrorBoundaryContent from '../../components/ErrorBoundaryContent';
import HelpFooter from '../../components/HelpFooter';

const configHelper = new ConfigHelper(configJson);

const connector = new HeApiConnector(configHelper.getEndpoints('TEACHING'));

const config = {
  alwaysSearchOnInitialLoad: true,
  apiConnector: connector,
  initialState: {
    resultsPerPage: configHelper.getPageSizeOptions()[0],
  },
  searchQuery: {
    facets: configHelper.buildFacetConfigFromConfig(),
    ...configHelper.buildSearchOptionsFromConfig(),
    disjunctiveFacets: configHelper.buildDisjunctiveFacetsFromConfig(),
  },
};

export default function TeachingActivitiesSearch() {
  return (
    <SearchProvider config={config}>
      <ConnectorProvider connector={connector}>
        <DocumentMetaUpdater searchName="Teaching Activities" />
        <WithSearch
          mapContextToProps={({
            clearFilters,
            facets,
            filters,
            isLoading,
            wasSearched,
            totalResults,
            resultSearchTerm,
          }) => ({
            clearFilters,
            facets,
            filters,
            isLoading,
            wasSearched,
            totalResults,
            resultSearchTerm,
          })}
        >
          {({
            clearFilters,
            facets,
            filters,
            isLoading,
            wasSearched,
            totalResults,
          }) => {
            return (
              <div className="search-ui" id="main-content">
                <ErrorBoundary view={ErrorBoundaryContent}>
                  <Layout
                    BodyContent={
                      <AppSearchResults
                        resultView={TeachingActivitiesSearchResultView}
                        shouldTrackClickThrough={true}
                        thumbnailField={configHelper.getThumbnailField()}
                        titleField={configHelper.getTitleField()}
                        urlField={configHelper.getUrlField()}
                        view={Results}
                      />
                    }
                    BodyFooter={
                      <>
                        <PagingRealistic />
                        <HelpFooter />
                      </>
                    }
                    BodyHeader={
                      <ResultHeader
                        configHelper={configHelper}
                        connector={connector}
                        filters={filters}
                        hideViewButtons={true}
                        totalResults={totalResults}
                        wasSearched={wasSearched}
                      />
                    }
                    Filters={<TeachingActivitiesSearchFilters />}
                    Header={
                      <Header
                        FiltersButton={
                          <FiltersButton
                            SearchFilters={<TeachingActivitiesSearchFilters />}
                            clearFilters={clearFilters}
                            facets={facets}
                          />
                        }
                      />
                    }
                    NoResults={<NoResults />}
                    filters={filters}
                    isLoading={isLoading}
                    searchType="teaching-activities"
                    totalResults={totalResults}
                    wasSearched={wasSearched}
                  />
                </ErrorBoundary>
              </div>
            );
          }}
        </WithSearch>
      </ConnectorProvider>
    </SearchProvider>
  );
}
